/**
 * Universal Link / App Link handling for the Capacitor shell.
 *
 * When the OS opens the app via an https://app.turnkey.coach/… link (a push
 * tap, a shared link, an email link), Capacitor fires `appUrlOpen` with that
 * URL. Because the shell is a remote web view already sitting on
 * app.turnkey.coach, we navigate the in-app Inertia router to the link's path
 * rather than triggering a fresh full load — so the deep link lands on the right
 * screen without a white reload.
 *
 * This is the in-app half of deep linking; it only fires once the OS is
 * configured to route those links to the app (iOS Associated Domains entitlement
 * + apple-app-site-association; Android intent-filter + assetlinks.json). Until
 * then it simply never receives events. No-op off the Capacitor shell; never
 * throws.
 *
 * @capacitor/app is loaded lazily (dynamic import) so it is excluded from the
 * main entry bundle and never downloaded/parsed in MobiLoud or plain web —
 * only fetched when isCapacitorEnvironment() is true.
 */

import { router } from "@inertiajs/react";
import Honeybadger from "@honeybadger-io/js";
import { isCapacitorEnvironment } from "./detection";

// Only follow links to our own hosts into the app; anything else is ignored
// (it would already have opened in the system browser).
const ALLOWED_HOSTS = ["app.turnkey.coach", "demo.turnkey.coach"];

function reportDeepLinkError(error) {
  try {
    Honeybadger.notify(error, {
      context: { component: "capacitor_deep_links" },
    });
  } catch {
    // reporting must never break the app
  }
}

/**
 * Extract the in-app path (pathname + query + hash) from a deep-link URL, or
 * null if it isn't one of our hosts / isn't parseable.
 */
export function pathFromDeepLink(url, allowedHosts = ALLOWED_HOSTS) {
  try {
    const parsed = new URL(url);
    if (!allowedHosts.includes(parsed.hostname)) {
      return null;
    }
    // Reject protocol-relative ("//evil.com") or backslash ("/\\evil.com")
    // paths: even though the host check passed, router.visit / location.assign
    // treat those as off-origin and would navigate the web view away to a
    // phishing site. A safe same-origin path starts with a single "/".
    if (/^\/[/\\]/.test(parsed.pathname)) {
      return null;
    }
    return `${parsed.pathname}${parsed.search}${parsed.hash}`;
  } catch {
    return null;
  }
}

// Mirror of PushNotifications::Dispatcher::NATIVE_CONVERSATION_PATH and
// ::NATIVE_WORKOUT_PATH. A push payload's `data.deep_link` is the NATIVE mobile
// route (the Flutter app's own router consumes it); two of its shapes have no
// Rails web route, so navigating the web view to them hits the 404 catch-all:
//
//   /conversations/<id> -> /conversations?id=<id>  (the chat SPA reads "?id=",
//                                                   for DMs and channels alike)
//   /workouts/<id>      -> /workout/<id>           (the web route is singular)
//
// The server now rewrites these before it sends the launch url, so this is a
// second line of defence — and it is not redundant: notifications already
// sitting in a device's tray were built BEFORE that deploy and still carry the
// native form, so a tap on one would otherwise still 404.
const NATIVE_CONVERSATION_PATH = /^\/conversations\/(\d+)$/;
const NATIVE_WORKOUT_PATH = /^\/workouts\/(\d+)$/;

function toWebPath(path) {
  const queryIndex = path.indexOf("?");
  const base = queryIndex === -1 ? path : path.slice(0, queryIndex);
  const query = queryIndex === -1 ? "" : path.slice(queryIndex + 1);

  const conversation = NATIVE_CONVERSATION_PATH.exec(base);
  if (conversation) {
    // `id` is itself a query param here, so an existing query string (e.g.
    // app_notification_id, which drives mark-as-read) joins as another pair.
    return `/conversations?id=${conversation[1]}${query ? `&${query}` : ""}`;
  }

  const workout = NATIVE_WORKOUT_PATH.exec(base);
  if (workout) {
    return `/workout/${workout[1]}${query ? `?${query}` : ""}`;
  }

  return path;
}

/**
 * Resolve a deep-link target to an in-app path. Accepts either an absolute URL
 * on one of our hosts (Universal/App Link, OneSignal `launchURL`) or an
 * app-relative path (`data.deep_link`, e.g. "/conversations/8"), so a single
 * helper covers every way a destination reaches us. Native-only path shapes are
 * rewritten to their web equivalents — see toWebPath.
 *
 * @returns {string|null} the in-app path, or null if it isn't safely ours
 */
export function resolveDeepLinkPath(target, allowedHosts = ALLOWED_HOSTS) {
  if (typeof target !== "string" || target.length === 0) {
    return null;
  }
  if (target.startsWith("/")) {
    // Same guard as pathFromDeepLink: reject protocol-relative ("//evil.com")
    // and backslash ("/\\evil.com") forms, which router.visit / location.assign
    // treat as off-origin and would navigate the web view to a phishing site.
    if (/^\/[/\\]/.test(target)) {
      return null;
    }
    return toWebPath(target);
  }
  const path = pathFromDeepLink(target, allowedHosts);
  return path === null ? null : toWebPath(path);
}

function navigateTo(path) {
  try {
    router.visit(path);
  } catch (error) {
    // If Inertia isn't ready for some reason, fall back to a plain navigation
    // (still within the web view, still on our origin).
    reportDeepLinkError(error);
    try {
      window.location.assign(path);
    } catch {
      // give up silently rather than throw out of an event handler
    }
  }
}

/**
 * Navigate the in-app router to a deep-link target (absolute URL or relative
 * path). Used by the push notification click handler, which must route the tap
 * itself rather than relying on the OS Universal-Link hop. Never throws.
 *
 * @returns {boolean} whether it navigated
 */
export function navigateToDeepLink(target) {
  const path = resolveDeepLinkPath(target);
  if (!path) {
    return false;
  }
  navigateTo(path);
  return true;
}

let registered = false;

/**
 * Route incoming Universal/App Links to the in-app router. No-op off-shell;
 * runs once; never throws.
 */
export function registerDeepLinks() {
  if (!isCapacitorEnvironment() || registered) {
    return;
  }
  registered = true;
  import("@capacitor/app")
    .then(({ App }) => {
      try {
        const result = App.addListener("appUrlOpen", ({ url }) => {
          // resolveDeepLinkPath rather than pathFromDeepLink: an OS-delivered
          // Universal Link can carry a pre-fix native path (an old push's
          // launchURL), which still needs rewriting to a routable web path.
          const path = resolveDeepLinkPath(url);
          if (path) {
            navigateTo(path);
          }
        });
        if (result && typeof result.catch === "function") {
          result.catch(reportDeepLinkError);
        }
      } catch (error) {
        registered = false; // allow a later call to retry
        reportDeepLinkError(error);
      }
    })
    .catch((error) => {
      registered = false; // allow a later call to retry
      reportDeepLinkError(error);
    });
}
